/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, {
  useMemo, useRef, useEffect, useLayoutEffect, useState, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import MonthDayDroppableCell from './MonthDayDroppableCell';
import MonthEditPopover from './MonthEditPopover';
import MonthEditPopoverContext from './MonthEditPopoverContext';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useCalendarKeyboardScroll from '../../hooks/useCalendarKeyboardScroll';
import {
  removePresaveItemFromRedux,
  setSelectedItems,
  setMonthSelectionActiveDate,
  setMonthViewWindowStart,
  setSelectedDate,
  initMonthViewWindow,
} from '../../ducks/calendarActions';
import { flushDebounceForTable } from './hooks/debounceHandler';
import { store } from '../../../../redux_setup/store';
import {
  pickMostVisibleMonthKey, getAdjacentMonthStart, getCollisionZone, MONTH_VIEW_WEEKS,
} from '../../utils/monthViewWindow';

const MonthContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #EEEEEE;
  height: 100%;
  width: 100%;
  /** Prevent the browser's native Shift+Click range text-selection from
   * highlighting the whole page when building a multi-selection. The edit
   * popover is portaled outside this container, so its inputs stay
   * selectable/editable. */
  user-select: none;
  /* Both axes scroll on this single container so it's the unique
   * "closest scrollable ancestor" rbd detects for our Droppables.
   * rbd watches its scroll events and recomputes droppable positions —
   * if vertical scroll happened on an ancestor outside rbd's awareness
   * (e.g. the page-level LayoutContainer), drops on newly-revealed
   * cells would land on stale positions. */
  overflow: auto;
`;

const ScrollIconBox = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid #EEEEEE;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  
   &:hover {
    border-color: #bdbdbd;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: #F5F5F5;
    transform: scale(0.96);
  }

  left: ${(props) => {
    if (!props.leftAnchor) return 'auto';
    return props.sidebarOpen ? '444px' : '60px';
  }};
  ${(props) => (props.leftAnchor
    ? `@media screen and (max-width: 1023px) { left: ${props.sidebarOpen ? '360px' : '60px'}; }`
    : '')}

  @media screen and (max-width: 767px) {
    ${(props) => (props.sidebarOpen ? 'display: none;' : '')}
  }
`;

const WeekRow = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 8px;
  /* Rows must NOT stretch to fill the container. With flex:1 the week rows grow
   * past their day-cells' 200px min-height to fill a tall/zoomed-out viewport,
   * which collapses the grid's scroll overflow — and the scroll-collision
   * loader needs the content to stay taller than the viewport to keep firing on
   * edge hits. Fixed to natural height (min one day-cell tall) so scrollHeight
   * is deterministic (~weekCount x 208px) and independent of viewport height. */
  flex: 0 0 auto;
  min-height: 200px;
`;

/** Floating pill shown while a window-shift (or initial) fetch is in flight.
 * Replaces the per-day shimmer: data is appended in place as it arrives and
 * this indicator is the only loading affordance, so the grid never flickers. */
const LoadingMoreIndicator = styled('div')`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 9999px;
  background: white;
  color: #444444;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(48, 49, 51, 0.18), 0 0 1px 0 rgba(48, 49, 51, 0.1);
  pointer-events: none;
`;

const Spinner = styled('div')`
  width: 14px;
  height: 14px;
  border: 2px solid #E0E0E0;
  border-top-color: #9E9E9E;
  border-radius: 50%;
  animation: month-loading-spin 0.7s linear infinite;
  @keyframes month-loading-spin {
    to { transform: rotate(360deg); }
  }
`;

/** The window's top buffer is 2 weeks, so the focus (current/selected) week is
 * always the 3rd row regardless of the total window length. */
const FOCUS_WEEK_INDEX = 2;

const MonthView = ({ isMonthItemsLoading }) => {
  const monthContainerRef = useRef(null);
  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const leftRafId = useRef(null);
  const rightRafId = useRef(null);

  /** Rolling-window scroll-collision bookkeeping.
   *  - isShiftingRef: true between dispatching a window shift and the
   *    re-anchor in useLayoutEffect, so the programmatic scrollTop write
   *    doesn't re-trigger a collision.
   *  - lastCollisionRef: timestamp of the last collision, debounces flings.
   *  - pendingAnchorRef: the week (by date key) to pin and the viewport
   *    offset it held before the shift, so content doesn't jump. */
  const isShiftingRef = useRef(false);
  const lastCollisionRef = useRef(0);
  const pendingAnchorRef = useRef(null);
  /** Last observed scrollTop, so a collision only fires when the user is
   * actually moving toward that edge (a down-scroll whose first event lands at
   * scrollTop 0 must not read as a ceiling hit). */
  const prevScrollTopRef = useRef(0);

  /** The focus week (by date key) to scroll to the viewport top, set on a
   * focus-centering event and re-applied once after that window's data loads
   * (day-cell heights grow as exercises land, pushing the focus week down). */
  const pendingFocusKeyRef = useRef(null);

  /** Keep the loading pill on screen for at least MIN_INDICATOR_MS so a fast
   * fetch doesn't flash it for a single frame. The label reflects why we're
   * loading: a scroll into a new window ("Loading more weeks..."), a paste
   * refetching the current window ("Pasting items...", see monthViewRefetchToken),
   * or a full data load after the store was cleared ("Loading Data...", e.g.
   * selecting a calendar — see storeClearedRef). */
  const MIN_INDICATOR_MS = 1000;
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Loading more weeks...');
  const loadingStartedAtRef = useRef(0);
  /** Latest paste-refetch token (updated every render) vs the one consumed by
   * the last load cycle, so we can tell a paste-driven load from a scroll. */
  const latestRefetchTokenRef = useRef(0);
  const consumedRefetchTokenRef = useRef(0);
  /** True when the item store was just cleared (calendarItemsDateRange nulled),
   * meaning the current fetch is a full data load — e.g. selecting a calendar —
   * rather than a scroll. Assigned from a selector further down; read here so
   * the loading effect can label the load "Loading Data". */
  const storeClearedRef = useRef(false);

  useEffect(() => {
    if (isMonthItemsLoading) {
      const isPasteLoad = consumedRefetchTokenRef.current !== latestRefetchTokenRef.current;
      consumedRefetchTokenRef.current = latestRefetchTokenRef.current;
      /** Priority: a paste bumps the refetch token; a cleared store (calendar
       * selection / first load) is a full data load; otherwise it's a scroll
       * into a new window. */
      let nextLabel = 'Loading more weeks...';
      if (isPasteLoad) {
        nextLabel = 'Pasting items...';
      } else if (storeClearedRef.current) {
        nextLabel = 'Loading Data...';
      }
      setLoadingLabel(nextLabel);
      loadingStartedAtRef.current = Date.now();
      setShowLoadingIndicator(true);
      return undefined;
    }
    const elapsed = Date.now() - loadingStartedAtRef.current;
    const remaining = Math.max(0, MIN_INDICATOR_MS - elapsed);
    const timeoutId = setTimeout(() => setShowLoadingIndicator(false), remaining);
    return () => clearTimeout(timeoutId);
  }, [isMonthItemsLoading]);

  useCalendarKeyboardScroll({
    getScrollNode: () => monthContainerRef.current,
    isActive: true,
  });

  const monthViewWindowStart = useSelector(
    (state) => state.calendar.data.monthViewWindowStart,
  );
  const monthViewFocusToken = useSelector(
    (state) => state.calendar.data.monthViewFocusToken,
  );
  const monthViewNavRequest = useSelector(
    (state) => state.calendar.data.monthViewNavRequest,
  );
  /** Latest window start, read by the focus effect without making it a dep —
   * so the effect fires only on a focus token bump, never on arrow/collision
   * window shifts. */
  const windowStartRef = useRef(monthViewWindowStart);
  windowStartRef.current = monthViewWindowStart;
  /** Bumped when a paste refetches the current window; read by the loading
   * indicator effect (via latestRefetchTokenRef) to label the load correctly. */
  const monthViewRefetchToken = useSelector(
    (state) => state.calendar.data.monthViewRefetchToken,
  );
  latestRefetchTokenRef.current = monthViewRefetchToken;
  /** A null earliest means the item store was just cleared (e.g. selecting a
   * calendar in SelectCalendarModal wipes calendarItems), so the ensuing fetch
   * is a full data load rather than a scroll/window shift. */
  const calendarItemsDateRange = useSelector(
    (state) => state.calendar.data.calendarItemsDateRange,
  );
  storeClearedRef.current = !calendarItemsDateRange?.earliest;
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const programType = useSelector((state) => state.calendar.data.programType);
  const isAnalyticsDisabled = useSelector(
    (state) => state.calendar.ui.isAnalyticsDisabled,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const dragIsInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );
  const mostRecentlyAddedCalendarItem = useSelector(
    (state) => state.calendar.data.mostRecentlyAddedCalendarItem,
  );

  const dispatch = useDispatch();

  /** Single popover instance for the whole month grid. Clicking another
   * exercise replaces `editingItem` so the popover re-anchors instead
   * of stacking. We store only the identifying bits (assignedId, date,
   * anchor); the live exercise content is looked up from Redux below
   * so swaps/edits to the item update the popover too. */
  const [editingItem, setEditingItem] = useState(null);

  /** Refs that let closePopover read the latest values without
   * recreating on every liveContent change. Updated each render. */
  const liveContentRef = useRef(null);
  const editingItemRef = useRef(null);

  const closePopover = useCallback(() => {
    const item = liveContentRef.current;
    const editing = editingItemRef.current;

    /** Flush any in-flight debounced table save so changes the coach
     * made (e.g. editing reps mid-keystroke) land immediately rather
     * than firing later without context. */
    if (item?.assignedId) {
      flushDebounceForTable(item.assignedId);
    }

    /** If the popover is closing on an unsaved preSave item (the user
     * added via "+" but didn't save), clean it up. Without this the
     * item stays as an orphaned preSave row with no visual indicator. */
    if (item?.preSave && editing) {
      dispatch(removePresaveItemFromRedux(
        moment(editing.date).format('YYYY-MM-DD'),
        null,
        item.assignedId,
      ));
    }
    setEditingItem(null);
  }, [dispatch]);

  /** On entering month view, seed the active day to today so a day is
   * always active without requiring a click. MonthView mounts fresh each
   * time month view is (re)entered and SET_VIEW_TYPE clears
   * monthSelectionActiveDate to null, so this runs on every entry. Guard
   * on the current value so we never clobber an already-set active day. */
  useEffect(() => {
    if (!store.getState().calendar.data.monthSelectionActiveDate) {
      dispatch(setMonthSelectionActiveDate(moment().format('YYYY-MM-DD')));
    }
  }, []);

  /** Mousedown anywhere in the grid that isn't an exercise row or the
   * selection action bar. Item rows own their own active-day/selection
   * handling (and we must NOT touch monthSelectionActiveDate before the
   * item's different-day check reads it), so bail for those. Otherwise:
   *  - empty space inside a day cell → make that day active, clear selection
   *  - the grid background / gaps → clear selection and active day
   * Shift is a no-op here (selection building happens on item rows). */
  const handleGridMouseDown = useCallback((e) => {
    if (e.shiftKey) return;
    if (e.target.closest('[data-assigned-id]')) return;
    if (e.target.closest('[data-month-action-bar]')) return;
    /** reactjs-popup (e.g. the bulk Superset picker) portals its content into
     * #popup-root on document.body. React still bubbles the synthetic event
     * through the React tree to this handler, but e.target lives in the
     * portal — so the DOM-based [data-month-action-bar] guard above misses
     * it. Bail for any click originating inside a portaled popup so picking a
     * superset letter (or any popup action) never clears the selection. */
    if (e.target.closest('#popup-root')) return;
    /** The day-header three-dot menu (rc-dropdown) portals its overlay to
     * document.body; the synthetic mousedown still bubbles through the React
     * tree to this handler, but e.target lives in the portal — so the day-cell
     * check below misses and would clear the active day. Bail for any mousedown
     * inside the dropdown overlay (.rc-menu also covers its color submenu). */
    if (e.target.closest('.rc-dropdown, .rc-menu')) return;

    const { selectedItems } = store.getState().calendar.data;
    const hasSelection = selectedItems && Object.keys(selectedItems).length > 0;
    const dayCell = e.target.closest('[data-month-day-cell]');

    if (dayCell) {
      if (hasSelection) dispatch(setSelectedItems({}));
      dispatch(setMonthSelectionActiveDate(dayCell.getAttribute('data-date')));
    } else {
      if (hasSelection) dispatch(setSelectedItems({}));
      dispatch(setMonthSelectionActiveDate(null));
    }
  }, [dispatch]);

  const popoverContextValue = useMemo(() => ({
    openPopover: ({ content, date, anchorEl }) => {
      if (!anchorEl || !content) return;
      /** Clicking the row whose popover is already open toggles it
       * closed — matches users' expectation that the row click is a
       * toggle, not a no-op. */
      if (editingItem?.assignedId === content.assignedId) {
        setEditingItem(null);
        return;
      }
      setEditingItem({ assignedId: content.assignedId, date, anchorEl });
    },
    activeAssignedId: editingItem?.assignedId ?? null,
    /** Lets the shared ReadOnlyExerciseItem month-gate its Shift+Click
     * multi-select behavior without affecting day/week (the default
     * context value has isMonthView false). */
    isMonthView: true,
  }), [editingItem]);

  const editingDateKey = editingItem
    ? moment(editingItem.date).format('YYYY-MM-DD')
    : null;
  const liveExercises = editingDateKey
    ? calendarItems[editingDateKey]?.exercises
    : null;
  const liveContentIndex = liveExercises
    ? liveExercises.findIndex((ex) => ex.assignedId === editingItem.assignedId)
    : -1;
  const liveContent = liveContentIndex >= 0 ? liveExercises[liveContentIndex] : null;

  /** Keep refs in sync each render so closePopover can read the latest
   * values without a stale closure. */
  liveContentRef.current = liveContent;
  editingItemRef.current = editingItem;

  /** If the item we were editing disappears (e.g. deleted), close the
   * popover so we don't render stale content. */
  useEffect(() => {
    if (editingItem && !liveContent) {
      setEditingItem(null);
    }
  }, [editingItem, liveContent]);

  /** Auto-open the preSave popover when "Create New Exercise" dispatches
   * a new item via the modal (the picker's own open-flag was cleared by
   * the modal overlay before the item landed). Scoped to preSave items
   * only — does not attempt to keep the popover open on swaps. */
  useEffect(() => {
    if (!mostRecentlyAddedCalendarItem?.preSave) return;
    if (editingItem?.assignedId === mostRecentlyAddedCalendarItem.assignedId) return;
    const dateKey = mostRecentlyAddedCalendarItem.date;
    if (!dateKey) return;
    requestAnimationFrame(() => {
      const row = document.querySelector(
        `[data-assigned-id="${mostRecentlyAddedCalendarItem.assignedId}"]`,
      );
      if (!row) return;
      setEditingItem({
        assignedId: mostRecentlyAddedCalendarItem.assignedId,
        date: moment(dateKey),
        anchorEl: row,
      });
    });
  }, [mostRecentlyAddedCalendarItem]);

  const scrollLeftStep = () => {
    const el = monthContainerRef.current;
    if (!el) return;

    if (el.scrollLeft > 0) {
      el.scrollBy({ left: -5 });
      leftRafId.current = requestAnimationFrame(scrollLeftStep);
    } else {
      leftRafId.current = null;
    }
  };

  const scrollRightStep = () => {
    const el = monthContainerRef.current;
    if (el.scrollLeft + el.clientWidth < el.scrollWidth) {
      el.scrollBy({ left: 5 });
      rightRafId.current = requestAnimationFrame(scrollRightStep);
    } else {
      rightRafId.current = null;
    }
  };

  const handleLeftMouseDown = () => {
    if (!leftRafId.current) {
      leftRafId.current = requestAnimationFrame(scrollLeftStep);
    }
  };

  const handleLeftMouseUp = () => {
    if (leftRafId.current) {
      cancelAnimationFrame(leftRafId.current);
      leftRafId.current = null;
    }
  };

  const handleRightMouseDown = () => {
    if (!rightRafId.current) {
      rightRafId.current = requestAnimationFrame(scrollRightStep);
    }
  };

  const handleRightMouseUp = () => {
    if (rightRafId.current) {
      cancelAnimationFrame(rightRafId.current);
      rightRafId.current = null;
    }
  };

  /** Drag-time autoscroll.
   *
   * rbd's built-in autoscroll has trouble with nested scroll containers,
   * and hooking edge-arrow buttons via hover doesn't work either —
   * during a drag the dragged element follows the cursor and sits on
   * top of the arrows, so mouseenter never fires on them.
   *
   * Instead, while a drag is active we listen for mousemove on the
   * document, measure the cursor's distance from each MonthContainer
   * edge, and drive our own RAF scroll loop. Speed ramps up as the
   * cursor approaches the edge. Both axes scroll on MonthContainer
   * itself — the same element rbd treats as the closest scrollable
   * ancestor of our Droppables — so rbd recomputes droppable positions
   * as content scrolls and drops land on the right cell. */
  useEffect(() => {
    if (!dragIsInProgress) return undefined;

    const EDGE_THRESHOLD = 80; /** px from edge where autoscroll kicks in */
    const MAX_SPEED = 18; /** px per frame at the very edge */
    let rafId = null;
    let speedX = 0;
    let speedY = 0;

    const tick = () => {
      const monthEl = monthContainerRef.current;
      if (!monthEl) {
        rafId = null;
        return;
      }
      if (speedX !== 0) monthEl.scrollBy({ left: speedX });
      if (speedY !== 0) monthEl.scrollBy({ top: speedY });
      if (speedX !== 0 || speedY !== 0) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };

    const computeSpeed = (distance) => {
      const ratio = 1 - distance / EDGE_THRESHOLD;
      return Math.ceil(MAX_SPEED * ratio);
    };

    const onMouseMove = (event) => {
      const monthEl = monthContainerRef.current;
      if (!monthEl) return;

      /** Use MonthContainer's own bounds so the trigger zones live at
       * the calendar's edges, not the viewport's. */
      const rect = monthEl.getBoundingClientRect();
      const { clientX, clientY } = event;
      const distLeft = clientX - rect.left;
      const distRight = rect.right - clientX;
      const distTop = clientY - rect.top;
      const distBottom = rect.bottom - clientY;

      const insideX = clientX >= rect.left && clientX <= rect.right;
      const insideY = clientY >= rect.top && clientY <= rect.bottom;

      if (insideY && distLeft >= 0 && distLeft < EDGE_THRESHOLD) {
        speedX = -computeSpeed(distLeft);
      } else if (insideY && distRight >= 0 && distRight < EDGE_THRESHOLD) {
        speedX = computeSpeed(distRight);
      } else {
        speedX = 0;
      }

      if (insideX && distTop >= 0 && distTop < EDGE_THRESHOLD) {
        speedY = -computeSpeed(distTop);
      } else if (insideX && distBottom >= 0 && distBottom < EDGE_THRESHOLD) {
        speedY = computeSpeed(distBottom);
      } else {
        speedY = 0;
      }

      if ((speedX !== 0 || speedY !== 0) && !rafId) {
        rafId = requestAnimationFrame(tick);
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [dragIsInProgress]);

  useEffect(() => {
    const checkOverflow = () => {
      const el = monthContainerRef.current;
      if (!el) return;

      if (leftScrollRef.current) {
        leftScrollRef.current.style.visibility = el.scrollLeft > 0 ? 'visible' : 'hidden';
      }

      if (rightScrollRef.current) {
        rightScrollRef.current.style.visibility = el.scrollLeft + el.clientWidth < el.scrollWidth
          ? 'visible'
          : 'hidden';
      }
    };

    checkOverflow();

    const el = monthContainerRef.current;
    el.addEventListener('scroll', checkOverflow);
    window.addEventListener('resize', checkOverflow);

    return () => {
      el.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  /** A fixed 10-week (70-day) rolling window anchored at monthViewWindowStart
   * (the Sunday of the top week), decoupled from calendar-month boundaries.
   * Scrolling into the ceiling/floor shifts the anchor by 2 weeks (see the
   * scroll-collision effect below). */
  const weeks = useMemo(() => {
    const start = moment(monthViewWindowStart, 'YYYY-MM-DD').startOf('week');
    const result = [];
    for (let w = 0; w < MONTH_VIEW_WEEKS; w += 1) {
      const week = [];
      for (let d = 0; d < 7; d += 1) {
        week.push(start.clone().add(w * 7 + d, 'days'));
      }
      result.push(week);
    }
    return result;
  }, [monthViewWindowStart]);

  /** The month kept full-brightness: the calendar month occupying the most
   * day-cells currently in the viewport, so the highlight tracks what the user
   * is looking at as they scroll the 10-week window (which spans 3-4 months).
   * Seeded with the focus week's month (window start + the 2-week top buffer) as
   * a pre-layout fallback — used until the first measurement, and in tests where
   * layout metrics read 0 so pickMostVisibleMonthKey returns null. Updated by
   * updateFocusedMonth on scroll/resize and after window shifts / data loads. */
  const [focusedMonthKey, setFocusedMonthKey] = useState(
    () => moment(monthViewWindowStart, 'YYYY-MM-DD')
      .startOf('week')
      .add(FOCUS_WEEK_INDEX, 'weeks')
      .format('YYYY-MM'),
  );

  /** Measure the window's week rows against the current scroll position and set
   * the focused month to the one filling most of the viewport. Mirrors the
   * nav-arrow measurement below. Only writes state when the key actually changes,
   * so an unchanged value (the common per-scroll case) never re-renders. */
  const updateFocusedMonth = useCallback(() => {
    const el = monthContainerRef.current;
    if (!el) return;
    const rows = Array.from(el.querySelectorAll('[data-week-start]')).map((row) => ({
      weekStart: row.getAttribute('data-week-start'),
      top: row.offsetTop,
      height: row.offsetHeight,
    }));
    const next = pickMostVisibleMonthKey({
      rows,
      viewTop: el.scrollTop,
      viewBottom: el.scrollTop + el.clientHeight,
    });
    if (next) setFocusedMonthKey((prev) => (prev === next ? prev : next));
  }, []);

  /** Recompute the focused month as the user scrolls (and on resize). rAF-
   * throttled to at most one measurement per frame; setFocusedMonthKey only
   * writes on a real change, so re-renders happen only when the highlight flips
   * at a month boundary — not every frame. */
  useEffect(() => {
    const el = monthContainerRef.current;
    if (!el) return undefined;
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateFocusedMonth();
      });
    };
    updateFocusedMonth();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [updateFocusedMonth]);

  /** Data landing grows day-cell heights (shifting where month boundaries fall)
   * without changing scrollTop, so a scroll event never fires — recompute after
   * window shifts and load transitions too. One rAF later so the measurement
   * reads post-layout heights. (Entry/Today/picker recentering and the collision
   * re-anchor both write scrollTop, so those are already covered by the scroll
   * listener above.) */
  useEffect(() => {
    const rafId = requestAnimationFrame(updateFocusedMonth);
    return () => cancelAnimationFrame(rafId);
  }, [monthViewWindowStart, isMonthItemsLoading, updateFocusedMonth]);

  /** Record the week (by date key) currently under the viewport top and the
   * pixel offset it holds, so the layout effect can pin it back after the
   * window array reshuffles. offsetTop is measured relative to the scroll
   * container (its positioning context), so `offsetTop - scrollTop` is the
   * viewport-relative top. */
  const captureAnchor = useCallback((weekStartKey) => {
    const el = monthContainerRef.current;
    if (!el) return;
    const row = el.querySelector(`[data-week-start="${weekStartKey}"]`);
    if (!row) return;
    pendingAnchorRef.current = {
      weekStartKey,
      offsetBefore: row.offsetTop - el.scrollTop,
    };
  }, []);

  /** Scroll the given week to the top of the viewport. The focus week lands
   * mid-content (2 weeks above, 7 below), so this never trips the ceiling/floor
   * collision checks. */
  const scrollFocusToTop = useCallback((weekStartKey) => {
    const el = monthContainerRef.current;
    if (!el) return;
    const row = el.querySelector(`[data-week-start="${weekStartKey}"]`);
    if (!row) return;
    el.scrollTop = row.offsetTop;
  }, []);

  /** Scroll-collision: hitting the ceiling prepends 2 weeks (dropping the
   * bottom 2); hitting the floor appends 2 weeks (dropping the top 2). The
   * window stays exactly 10 weeks. We capture an anchor week first so the
   * layout effect keeps content from jumping. */
  useEffect(() => {
    const el = monthContainerRef.current;
    if (!el) return undefined;

    const DEBOUNCE_MS = 400;

    const onScroll = () => {
      const prevScrollTop = prevScrollTopRef.current;
      prevScrollTopRef.current = el.scrollTop;
      if (isShiftingRef.current || dragIsInProgress) return;
      const now = Date.now();
      if (now - lastCollisionRef.current < DEBOUNCE_MS) return;

      // Nothing to do when the content isn't tall enough to scroll.
      if (el.scrollHeight <= el.clientHeight) return;

      const maxScrollTop = el.scrollHeight - el.clientHeight;
      const zone = getCollisionZone(maxScrollTop);
      const movingUp = el.scrollTop < prevScrollTop;
      const movingDown = el.scrollTop > prevScrollTop;
      const atTop = el.scrollTop <= zone;
      const atBottom = el.scrollTop >= maxScrollTop - zone;

      if (atTop && movingUp) {
        // weeks[0] becomes weeks[2] after prepending 2 weeks.
        captureAnchor(weeks[0][0].format('YYYY-MM-DD'));
        isShiftingRef.current = true;
        lastCollisionRef.current = now;
        dispatch(setMonthViewWindowStart(
          moment(monthViewWindowStart, 'YYYY-MM-DD').subtract(2, 'weeks').format('YYYY-MM-DD'),
        ));
      } else if (atBottom && movingDown) {
        // weeks[2] becomes weeks[0] after dropping the top 2 weeks.
        captureAnchor(weeks[2][0].format('YYYY-MM-DD'));
        isShiftingRef.current = true;
        lastCollisionRef.current = now;
        dispatch(setMonthViewWindowStart(
          moment(monthViewWindowStart, 'YYYY-MM-DD').add(2, 'weeks').format('YYYY-MM-DD'),
        ));
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [monthViewWindowStart, weeks, dragIsInProgress, captureAnchor, dispatch]);

  /** After a scroll-collision shift re-renders the grid, pin the anchored week
   * back to the viewport offset it held so the content under the cursor doesn't
   * jump. Runs synchronously before paint. Arrow/Today/picker-driven shifts set
   * no pending anchor, so they intentionally jump. */
  useLayoutEffect(() => {
    const el = monthContainerRef.current;
    const pending = pendingAnchorRef.current;
    if (!el || !pending) {
      isShiftingRef.current = false;
      return;
    }
    const row = el.querySelector(`[data-week-start="${pending.weekStartKey}"]`);
    if (row) {
      let nextScrollTop = row.offsetTop - pending.offsetBefore;
      /** Never re-anchor onto (or inside) a collision edge: landing at 0/max, or
       * within the zone, leaves the user pinned with no travel so the next scroll
       * can immediately re-trigger — or, at the ceiling, undo — the shift. Bias
       * just *outside* the zone so a real edge-hit is required to shift again. Use
       * the same range-clamped zone as the collision check so a thin-overflow
       * window (where the two zones would otherwise meet) stays consistent. */
      const maxScrollTop = el.scrollHeight - el.clientHeight;
      const zone = getCollisionZone(maxScrollTop);
      if (nextScrollTop <= zone) {
        nextScrollTop = Math.min(zone + 1, maxScrollTop);
      } else if (nextScrollTop >= maxScrollTop - zone) {
        nextScrollTop = Math.max(maxScrollTop - zone - 1, 0);
      }
      el.scrollTop = nextScrollTop;
    }
    pendingAnchorRef.current = null;
    isShiftingRef.current = false;
  }, [monthViewWindowStart]);

  /** Focus-centering (entry / Today / picker day-select): scroll the focus week
   * (weeks[2]) to the top so the current/selected week is in view with its 2
   * preloaded weeks above it. Keyed only on the focus token — also runs on
   * mount — so arrow/collision window shifts never recenter. */
  useLayoutEffect(() => {
    const focusKey = moment(windowStartRef.current, 'YYYY-MM-DD')
      .startOf('week')
      .add(FOCUS_WEEK_INDEX, 'weeks')
      .format('YYYY-MM-DD');
    pendingFocusKeyRef.current = focusKey;
    scrollFocusToTop(focusKey);
  }, [monthViewFocusToken, scrollFocusToTop]);

  /** Re-apply the focus scroll once the focus window's data lands: day-cell
   * rows above the focus week grow as exercises render, which would otherwise
   * leave the focus week pushed below the top. Only fires for a pending focus
   * request, so scroll-collision fetches don't hijack the viewport. */
  useEffect(() => {
    if (isMonthItemsLoading || !pendingFocusKeyRef.current) return;
    scrollFocusToTop(pendingFocusKeyRef.current);
    pendingFocusKeyRef.current = null;
  }, [isMonthItemsLoading, scrollFocusToTop]);

  /** Header prev/next arrows request a month jump (monthViewNavRequest). Resolve
   * it against the month currently scrolled into view — the calendar month with
   * the most day-cells visible in the viewport — then jump to the 1st of the
   * adjacent month and recenter/scroll via initMonthViewWindow (which also sets
   * the focus token and triggers the window-keyed data fetch). Keyed only on the
   * request object (a fresh nonce per click), so it runs once per click and never
   * on mount (null). windowStartRef is read without a dep so a window shift
   * doesn't re-fire it. */
  /** Nonce of the last nav request we acted on. Lazily seeded with the
   * mount-time nonce so a request that persists in Redux across an unmount/
   * remount doesn't auto-navigate on mount — only a fresh click (higher nonce)
   * navigates. */
  const lastHandledNavNonceRef = useRef();
  if (lastHandledNavNonceRef.current === undefined) {
    lastHandledNavNonceRef.current = monthViewNavRequest?.nonce || 0;
  }

  useEffect(() => {
    if (!monthViewNavRequest) return;
    // Only act on a not-yet-handled request (also no-ops StrictMode double-invoke
    // and remounts of a persisted request).
    if (monthViewNavRequest.nonce === lastHandledNavNonceRef.current) return;
    lastHandledNavNonceRef.current = monthViewNavRequest.nonce;
    const { direction } = monthViewNavRequest;
    if (direction !== 'next' && direction !== 'prev') return;
    const el = monthContainerRef.current;
    if (!el) return;

    const rows = Array.from(el.querySelectorAll('[data-week-start]')).map((row) => ({
      weekStart: row.getAttribute('data-week-start'),
      top: row.offsetTop,
      height: row.offsetHeight,
    }));
    let currentMonthKey = pickMostVisibleMonthKey({
      rows,
      viewTop: el.scrollTop,
      viewBottom: el.scrollTop + el.clientHeight,
    });
    // Fallback to the focus week's month if nothing is measurable (e.g. before
    // layout is available).
    if (!currentMonthKey) {
      currentMonthKey = moment(windowStartRef.current, 'YYYY-MM-DD')
        .startOf('week')
        .add(FOCUS_WEEK_INDEX, 'weeks')
        .format('YYYY-MM');
    }

    const target = getAdjacentMonthStart(currentMonthKey, direction);
    dispatch(setSelectedDate(target));
    dispatch(initMonthViewWindow(target));
  }, [monthViewNavRequest, dispatch]);

  return (
    <MonthEditPopoverContext.Provider value={popoverContextValue}>
      <ScrollIconBox
        ref={leftScrollRef}
        leftAnchor
        sidebarOpen={isPrimaryAnimatedSideBarOpen}
        style={{
          position: 'fixed',
          top: '50%',
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleLeftMouseDown}
        onMouseUp={handleLeftMouseUp}
        onMouseLeave={handleLeftMouseUp}
      >
        <IcomoonReact iconSet={iconSet} size={14} icon='left-arrow' color='#9E9E9E' />
      </ScrollIconBox>

      <MonthContainer ref={monthContainerRef} onMouseDown={handleGridMouseDown}>
        {/* The month currently occupying most of the viewport stays full-
            brightness (scroll-tracked, see focusedMonthKey); the other months
            are dimmed so the month in view always reads as the current one. */}
        {weeks.map((week) => (
          <WeekRow
            key={week[0].format('YYYY-MM-DD')}
            data-week-start={week[0].format('YYYY-MM-DD')}
          >
            {week.map((date) => {
              const dateKey = date.format('YYYY-MM-DD');
              const dayData = calendarItems[dateKey];
              // Dim days outside the focused (most-visible) month so the month
              // in view always reads as the brighter one.
              const isShadedMonth = date.format('YYYY-MM') !== focusedMonthKey;

              return (
                <MonthDayDroppableCell
                  key={dateKey}
                  date={date}
                  dayData={dayData}
                  isPaddingDay={false}
                  isShadedMonth={isShadedMonth}
                  // Month view never shimmers individual days — data is
                  // appended in place and the floating indicator below signals
                  // that more weeks are loading.
                  isMonthItemsLoading={false}
                  programType={programType}
                  isAnalyticsDisabled={isAnalyticsDisabled}
                />
              );
            })}
          </WeekRow>
        ))}
      </MonthContainer>

      <ScrollIconBox
        ref={rightScrollRef}
        sidebarOpen={isPrimaryAnimatedSideBarOpen}
        style={{
          position: 'fixed',
          top: '50%',
          right: 12,
          zIndex: 1000,
          visibility: 'hidden',
        }}
        onMouseDown={handleRightMouseDown}
        onMouseUp={handleRightMouseUp}
        onMouseLeave={handleRightMouseUp}
      >
        <IcomoonReact iconSet={iconSet} size={14} icon='right-arrow' color='#9E9E9E' />
      </ScrollIconBox>
      {editingItem && liveContent && (
        <MonthEditPopover
          key={editingItem.assignedId}
          content={liveContent}
          index={liveContentIndex}
          date={editingItem.date}
          anchorEl={editingItem.anchorEl}
          onClose={closePopover}
          scrollContainerRef={monthContainerRef}
        />
      )}
      {showLoadingIndicator && (
        <LoadingMoreIndicator>
          <Spinner />
          {loadingLabel}
        </LoadingMoreIndicator>
      )}
    </MonthEditPopoverContext.Provider>
  );
};

export default MonthView;
